import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import { useRef, useState, useEffect } from "react";
import "./global.css";
import "./normalize.css";

import useFiles from "./hooks/useFiles";
import "./hooks/useFiles.css";

import ScrollToTop from "./components/ScrollToTop";
import LeftNav from "./components/LeftNav";

import "./components/LeftNav.css";

import RightNav from "./components/RightNav";
import "./components/RightNav.css";

import Header from "./components/Header";
import "./components/Header.css";

import LoginBrowse from "./components/LoginBrowse";

import SearchOverlay from "./components/SearchOverlay";
import "./components/SearchOverlay.css";

import DocumentList from "./components/DocumentList";
import "./components/DocumentList.css";

import Footer from "./components/Footer";
import "./components/Footer.css";

import Login from "./components/LOGIN/Login";

import HomePage from "./components/HOMEPAGE/HomePage";
import "./components/HOMEPAGE/HomePage.css";
import SubjectDocuments from "./components/SUBJECTDOCUMENTS/SubjectDocuments";
import NotFound from "./components/NotFound";
import Faqs from "./components/FAQ/Faqs";
import useBlockBackNavigation from "./hooks/useBlockBackNavigation";
import FileUpload from "./components/UPLOAD/FileUpload";
import FileModeration from "./components/FILEMODERATION/FileModeration";
import ProtectedRoute from "./components/ProtectedRoute";
import Reports from "./components/REPORTS/Reports";
import Results from "./components/Results";
import ResetPassword from "./components/ResetPassword";

import Subjects from "./components/SUBJECTS/Subjects";
import "./components/SUBJECTS/Subjects.css";
import CreateAccount from "./components/REGISTER/CreateAccount";
import "./components/REGISTER/CreateAccount.css";
import GradePopover from "./components/GradePopover";

//One parent owns useFloating
//Child components receive refs + handlers as props
import {
  useFloating, //figures out position
  offset, //adds space between button and popup
  flip, //moves popup to another side if there’s no space
  shift, //nudges popup back inside the screen
  autoUpdate, //keeps it stuck to the button when things change
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from "@floating-ui/react";

import Popover from "./components/Popover";

export default function App() {
  const [leftNavOpen, setLeftNavOpen] = useState(false);
  const [rightNavOpen, setRightNavOpen] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [activeFileId, setActiveFileId] = useState(null);

  const [open, setOpen] = useState(false);
  const [referenceEl, setReferenceEl] = useState(null);

  const leftNavRef = useRef(null);
  const rightNavRef = useRef(null);
  const searchInputRef = useRef(null);

  const [showSearchLogo, setShowSearchLogo] = useState(true);
  const [showUploadIcon, setShowUploadIcon] = useState(false);
  const [showLoginCreate, setShowLoginCreate] = useState(true);
  const [showPCSearch, setShowPCSearch] = useState(true);

  // trigger that causes re-fetch / re-render when incremented
  const [ratingTrigger, setRatingTrigger] = useState(0);

  const openLeftNav = () => setLeftNavOpen(true);
  const closeLeftNav = () => setLeftNavOpen(false);

  const openRightNav = () => setRightNavOpen(true);
  const closeRightNav = () => setRightNavOpen(false);

  const openSearch = () => setSearchActive(true);
  const closeSearch = () => setSearchActive(false);

  const [hideElements, setHideElements] = useState(false);
  const [value, setValue] = useState("");
  const [selectedGradeRange, setSelectedGradeRange] = useState("");

  const { databaseNames, storage_path, file_rating, fileIds } = useFiles(
    ratingTrigger,
    setNotFound,
    selectedGradeRange
  ); //call useFiles with props (ratingTrigger, setNotFound)
  //return it as an object and then destructure

  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token") //quick trick to turn something into a true/false
  );

  useBlockBackNavigation(isAuthenticated);

  useEffect(() => {
    document.title = "Share2Teach";
    const token = localStorage.getItem("token"); //Check if there’s a saved token in the browser.
    if (token) {
      setIsAuthenticated(true); //If a token exists → mark the user as authenticated.
    }
  }, []);

  const setAuth = (boolean) => {
    setIsAuthenticated(boolean);
  };

  // callback passed to RightNav
  const handleRatingSubmitted = () => {
    setRatingTrigger((prev) => prev + 1);
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "left",
    middleware: [offset(-5), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  // Interactions
  const click = useClick(context);
  const dismiss = useDismiss(context); // ✅ click outside
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  useEffect(() => {
    refs.setReference(referenceEl); //Okay, position the popover relative to this element
  }, [referenceEl]);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route
          path="/"
          element={
            <div className="parent">
              <div className="App">
                <LeftNav
                  leftNavOpen={leftNavOpen}
                  closeLeftNav={closeLeftNav}
                  leftNavRef={leftNavRef}
                  isAuthenticated={isAuthenticated}
                  setAuth={setAuth}
                  onRatingSubmitted={handleRatingSubmitted}
                />

                <RightNav
                  rightNavOpen={rightNavOpen}
                  rightNavRef={rightNavRef}
                  closeRightNav={closeRightNav}
                  leftNavOpen={leftNavOpen}
                  closeLeftNav={closeLeftNav}
                  openRightNav={openRightNav}
                  activeFileId={activeFileId}
                  isAuthenticated={isAuthenticated}
                  onRatingSubmitted={handleRatingSubmitted} // <-- pass callback
                />

                {!searchActive && (
                  <>
                    <Header
                      openLeftNav={openLeftNav}
                      openSearch={openSearch}
                      rightNavOpen={rightNavOpen}
                      closeRightNav={closeRightNav}
                      leftNavOpen={leftNavOpen}
                      showSearchLogo={showSearchLogo}
                      showLoginCreate={showLoginCreate}
                      showPCSearch={showPCSearch}
                      setHideElements={setHideElements}
                      onRatingSubmitted={handleRatingSubmitted}
                      setValue={setValue}
                      isAuthenticated={isAuthenticated}
                    />

                    {!hideElements && (
                      <main className="main-content" id="main-content">
                        <LoginBrowse
                          rightNavOpen={rightNavOpen}
                          leftNavOpen={leftNavOpen}
                        />

                        <GradePopover
                          onSelect={(grade) => setSelectedGradeRange(grade)} //pass a method as a prop
                        />

                        {open && (
                          <Popover
                            ref={refs.setFloating}
                            style={floatingStyles}
                            fileId={activeFileId}
                            onRatingSubmitted={handleRatingSubmitted}
                          />
                        )}

                        {!notFound && (
                          <DocumentList
                            openRightNav={openRightNav}
                            databaseNames={databaseNames}
                            storage_path={storage_path}
                            file_rating={file_rating}
                            rightNavOpen={rightNavOpen}
                            leftNavOpen={leftNavOpen}
                            closeRightNav={closeRightNav}
                            closeLeftNav={closeLeftNav}
                            fileIds={fileIds} // 👈 must come from backend fetch
                            setActiveFileId={setActiveFileId}
                            onThreeDotClick={(e, fileId) => {
                              setReferenceEl(e.currentTarget);
                              setActiveFileId(fileId);
                              setOpen(true);
                            }}
                          />
                        )}
                      </main>
                    )}
                    {notFound && !hideElements && (
                      <>
                        <NotFound />
                      </>
                    )}

                    {!hideElements && (
                      <Footer
                        className="main-footer"
                        leftNavOpen={leftNavOpen}
                        closeLeftNav={closeLeftNav}
                        rightNavOpen={rightNavOpen}
                        closeRightNav={closeRightNav}
                      />
                    )}

                    {hideElements && (
                      <Results
                        openRightNav={openRightNav}
                        rightNavOpen={rightNavOpen}
                        leftNavOpen={leftNavOpen}
                        closeRightNav={closeRightNav}
                        closeLeftNav={closeLeftNav}
                        setActiveFileId={setActiveFileId}
                        value={value}
                      />
                    )}
                  </>
                )}

                <SearchOverlay
                  searchActive={searchActive}
                  closeSearch={closeSearch}
                  searchInputRef={searchInputRef}
                  openRightNav={openRightNav}
                  rightNavOpen={rightNavOpen}
                  leftNavOpen={leftNavOpen}
                  closeRightNav={closeRightNav}
                  closeLeftNav={closeLeftNav}
                  setActiveFileId={setActiveFileId}
                  rightNavRef={rightNavRef}
                  onRatingSubmitted={handleRatingSubmitted} // <-- pass callback
                  ratingTrigger={ratingTrigger}
                />
              </div>
            </div>
          }
        />

        <Route
          path="/login"
          element={
            <Login
              leftNavOpen={leftNavOpen}
              leftNavRef={leftNavRef}
              openLeftNav={openLeftNav}
              closeLeftNav={closeLeftNav}
              setAuth={setAuth}
              onRatingSubmitted={handleRatingSubmitted}
              isAuthenticated={isAuthenticated}
            />
          }
        />
        <Route
          path="/createAccount"
          element={
            <CreateAccount
              leftNavOpen={leftNavOpen}
              leftNavRef={leftNavRef}
              openLeftNav={openLeftNav}
              closeLeftNav={closeLeftNav}
              setAuth={setAuth}
              isAuthenticated={isAuthenticated}
            />
          }
        />

        <Route path="/reset-password/:token" element={<ResetPassword />} />

        <Route
          path="/subjects"
          element={
            <Subjects
              leftNavOpen={leftNavOpen}
              showSearchLogo={showSearchLogo}
              leftNavRef={leftNavRef}
              openLeftNav={openLeftNav}
              closeLeftNav={closeLeftNav}
              openSearch={openSearch}
              searchActive={searchActive}
              closeSearch={closeSearch}
              searchInputRef={searchInputRef}
              setActiveFileId={setActiveFileId}
              openRightNav={openRightNav}
              rightNavOpen={rightNavOpen}
              closeRightNav={closeRightNav}
              rightNavRef={rightNavRef}
              onRatingSubmitted={handleRatingSubmitted} // <-- pass callback
              ratingTrigger={ratingTrigger}
              activeFileId={activeFileId}
              isAuthenticated={isAuthenticated}
            />
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <HomePage
                isAuthenticated={isAuthenticated}
                setAuth={setAuth}
                leftNavRef={leftNavRef}
                openLeftNav={openLeftNav}
                closeLeftNav={closeLeftNav}
                rightNavRef={rightNavRef}
                activeFileId={activeFileId}
                fileIds={fileIds} // 👈 must come from backend fetch
                setActiveFileId={setActiveFileId}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents/:id"
          element={
            <SubjectDocuments
              isAuthenticated={isAuthenticated}
              setAuth={setAuth}
            />
          }
        />
        <Route
          path="/home/fileupload"
          element={
            <ProtectedRoute>
              <FileUpload
                leftNavOpen={leftNavOpen}
                openLeftNav={openLeftNav}
                openSearch={openSearch}
                rightNavOpen={rightNavOpen}
                closeRightNav={closeRightNav}
                isAuthenticated={isAuthenticated}
                setAuth={setAuth}
                onRatingSubmitted={handleRatingSubmitted}
                closeLeftNav={closeLeftNav}
                leftNavRef={leftNavRef}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home/reports"
          element={
            <ProtectedRoute>
              <Reports
                leftNavOpen={leftNavOpen}
                openLeftNav={openLeftNav}
                openSearch={openSearch}
                rightNavOpen={rightNavOpen}
                closeRightNav={closeRightNav}
                isAuthenticated={isAuthenticated}
                setAuth={setAuth}
                onRatingSubmitted={handleRatingSubmitted}
                closeLeftNav={closeLeftNav}
                leftNavRef={leftNavRef}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/home/filemoderation"
          element={
            <ProtectedRoute>
              <FileModeration
                leftNavOpen={leftNavOpen}
                openLeftNav={openLeftNav}
                openSearch={openSearch}
                rightNavOpen={rightNavOpen}
                closeRightNav={closeRightNav}
                isAuthenticated={isAuthenticated}
                setAuth={setAuth}
                onRatingSubmitted={handleRatingSubmitted}
                closeLeftNav={closeLeftNav}
                leftNavRef={leftNavRef}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faqs"
          element={
            <Faqs
              leftNavOpen={leftNavOpen}
              closeLeftNav={closeLeftNav}
              leftNavRef={leftNavRef}
              isAuthenticated={isAuthenticated}
              setAuth={setAuth}
              onRatingSubmitted={handleRatingSubmitted}
              openLeftNav={openLeftNav}
              openSearch={openSearch}
              rightNavOpen={rightNavOpen}
              closeRightNav={closeRightNav}
              searchActive={searchActive}
              closeSearch={closeSearch}
              searchInputRef={searchInputRef}
              openRightNav={openRightNav}
              setActiveFileId={setActiveFileId}
              rightNavRef={rightNavRef}
              ratingTrigger={ratingTrigger}
            />
          }
        />
      </Routes>
    </Router>
  );
}
